import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, CardHeader, Divider, Grid, Skeleton, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { CustomField } from '@bandido/shared/types';

import { useGetFieldBlockById, usePatchCustomFieldBlock } from '@frontend/features/custom-fields/queries';
import { DynamicFieldsProvider } from '@frontend/features/support-forms/components/DynamicFieldsContext';
import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { SchemaGenerator } from '@frontend/features/support-forms/components';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useGetWorkflowsByAgency } from '@frontend/features/workflows/queries';
import { QueryKeys } from '@frontend/features/common/types';
import { ControlledMultiSelect } from '@frontend/features/common/components/ControlledMultiSelect';
import { FieldBlock } from '@frontend/features/custom-fields/components/FieldBlock';

const resolver = yupResolver(
  yup.object().shape({
    name: yup.string().required('common.form.required'),
    workflows: yup.array().of(yup.string()).min(1, 'common.form.required').required('common.form.required'),
  }),
);

export const CustomFieldsView = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { fieldBlockId, id } = useParams();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const { data, isLoading } = useGetFieldBlockById({
    id: fieldBlockId,
  });
  const { data: workflows, isLoading: workflowsLoading } = useGetWorkflowsByAgency({
    agencyId: id,
    search: '',
  });
  const [fields, setFields] = useState<CustomField[]>([]);

  const { mutate: patch, isLoading: isSaving } = usePatchCustomFieldBlock({
    onSuccess: () => {
      addToast({
        color: 'success',
        message: t('customFields.fieldBlockEdit.editSuccess'),
      });
      queryClient.invalidateQueries([QueryKeys.getFieldBlockById, fieldBlockId]);
    },
    onError: () => {
      addToast({
        color: 'error',
        message: t('customFields.fieldBlockEdit.editError'),
      });
    },
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver });

  const handleUpdateFields = (newFields: CustomField[]) => {
    setFields(newFields);
  };

  const onSubmit = async (data: any) => {
    if (id && fieldBlockId) {
      patch({
        fieldBlockId,
        name: data.name,
        workflows: data.workflows,
        agencyId: +id,
        fields: fields,
      });
    }
  };

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        workflows: data.workflows,
      });
      try {
        setFields(data.fields as CustomField[]);
      } catch (error) {
        console.log('Error trying to parse custom fields:', error);
      }
    }
  }, [data]);

  return (
    <Box display="flex" gap={4} flexDirection="column">
      <Box>
        <Typography variant="h6" color={theme.palette.text.primary}>
          {data?.name}
        </Typography>
        <Typography variant="subtitle1" align="left" color={theme.palette.text.disabled}>
          {data?.agencyName}
        </Typography>
      </Box>

      <DynamicFieldsProvider fields={fields} onUpdateFields={handleUpdateFields}>
        <Grid container>
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                p: theme.spacing(3),
                boxShadow: 'none',
                borderRadius: theme.spacing(3),
              }}
              elevation={4}
            >
              <CardHeader title={<Typography variant="h6">{t('customFields.fieldBlockEdit.title')}</Typography>} />
              <CardContent>
                <Box
                  component="form"
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(3),
                    mt: theme.spacing(3),
                  }}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <ControlledTextField
                    required
                    fullWidth
                    control={control}
                    name="name"
                    label={t('customFields.fieldBlockEdit.name')}
                    placeholder={t('common.form.onlyLetters')}
                    errorMessage={errors.name?.message ? t(errors.name?.message) : undefined}
                  />

                  <ControlledMultiSelect
                    required
                    fullWidth
                    variant="outlined"
                    name="workflows"
                    size="small"
                    label="Workflows"
                    control={control}
                    disabled={workflowsLoading}
                    placeholder={t('common.form.select')}
                    errorMessage={errors.workflows?.message ? t(errors.workflows?.message) : undefined}
                    options={
                      workflows?.items.length
                        ? workflows.items.map(({ id, name }) => ({ value: id, label: name }))
                        : [{ value: 'empty', label: t('supportForms.view.noWorkflows') }]
                    }
                  />

                  <Divider sx={{ my: theme.spacing(2) }} />

                  <SchemaGenerator />

                  <Box display="flex" justifyContent="flex-end">
                    <LoadingButton
                      type="submit"
                      color="primary"
                      variant="contained"
                      disabled={isSaving}
                      loading={isSaving}
                    >
                      {t('common.save')}
                    </LoadingButton>
                  </Box>
                </Box>
              </CardContent>

              {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            {data && <FieldBlock editing title={data.name} />}
          </Grid>
        </Grid>
      </DynamicFieldsProvider>
    </Box>
  );
};
